import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Database, Users, Building2, Plus, Search, ChevronDown, ChevronRight, Edit2, Trash2, History, Archive, Upload, Download, Layers, FileStack, BookOpen, FileText } from 'lucide-react';
import TemplatesManager from '@/components/TemplatesManager';
import BriefTemplatesManager from '@/components/BriefTemplatesManager';
import { useConfirm } from '@/components/ConfirmDialog';
import DocumentTemplatesManager from '@/components/DocumentTemplatesManager';
import { useAuth } from '@/contexts/AuthContext';
import { isOwner } from '@/lib/perms';

const uomOptions = ['Sq.ft', 'Rft.', 'No.s', 'R.Mtr', 'Mtr'];

function formatINR(n) {
  if (!n) return '0';
  return Number(n).toLocaleString('en-IN');
}

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
}

// Item & Cost Sheet Component
function DBSpecDatabase() {
  const [confirm, confirmDialog] = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  const [showItemModal, setShowItemModal] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingVariant, setEditingVariant] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyVariant, setHistoryVariant] = useState(null);
  
  const [itemForm, setItemForm] = useState({ sr_no: 0, item_name: '', notes: '' });
  const [variantForm, setVariantForm] = useState({ specification: '', uom: 'Sq.ft', rate: 0 });

  const loadItems = useCallback(async () => {
    try {
      const res = await api.get('/db-items');
      setItems(res.data);
    } catch { toast.error('Failed to load Item & Cost Sheet'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  const seedDatabase = async () => {
    try {
      const res = await api.post('/db-items/seed');
      toast.success(res.data.message);
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Seed failed');
    }
  };

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const openAddItem = () => {
    setEditingItem(null);
    setItemForm({ sr_no: items.length + 1, item_name: '', notes: '' });
    setShowItemModal(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({ sr_no: item.sr_no, item_name: item.item_name, notes: item.notes || '' });
    setShowItemModal(true);
  };

  const saveItem = async () => {
    if (!itemForm.item_name) {
      toast.error('Item name is required');
      return;
    }
    try {
      if (editingItem) {
        await api.put(`/db-items/${editingItem.id}`, itemForm);
        toast.success('Item updated');
      } else {
        await api.post('/db-items', { ...itemForm, variants: [] });
        toast.success('Item created');
      }
      setShowItemModal(false);
      loadItems();
    } catch { toast.error('Failed to save item'); }
  };

  const archiveItem = async (itemId) => {
    if (!(await confirm({ title: 'Archive item?', message: 'It will be hidden from BOQ search. You can restore it from the archive.', confirmLabel: 'Archive' }))) return;
    try {
      await api.delete(`/db-items/${itemId}`);
      toast.success('Item archived');
      loadItems();
    } catch { toast.error('Failed to archive item'); }
  };

  const openAddVariant = (itemId) => {
    setSelectedItemId(itemId);
    setEditingVariant(null);
    setVariantForm({ specification: '', uom: 'Sq.ft', rate: 0 });
    setShowVariantModal(true);
  };

  const openEditVariant = (itemId, variant) => {
    setSelectedItemId(itemId);
    setEditingVariant(variant);
    setVariantForm({ specification: variant.specification, uom: variant.uom, rate: variant.rate });
    setShowVariantModal(true);
  };

  const saveVariant = async () => {
    if (!variantForm.specification) {
      toast.error('Specification is required');
      return;
    }
    try {
      if (editingVariant) {
        await api.put(`/db-items/${selectedItemId}/variants/${editingVariant.id}`, variantForm);
        toast.success('Variant updated');
      } else {
        await api.post(`/db-items/${selectedItemId}/variants`, variantForm);
        toast.success('Variant added');
      }
      setShowVariantModal(false);
      loadItems();
    } catch { toast.error('Failed to save variant'); }
  };

  const archiveVariant = async (itemId, variantId) => {
    if (!(await confirm({ title: 'Archive variant?', message: 'This specification will be hidden from BOQ search.', confirmLabel: 'Archive' }))) return;
    try {
      await api.delete(`/db-items/${itemId}/variants/${variantId}`);
      toast.success('Variant archived');
      loadItems();
    } catch { toast.error('Failed to archive variant'); }
  };

  const showRateHistory = (variant) => {
    setHistoryVariant(variant);
    setShowHistoryModal(true);
  };

  const filtered = items.filter(item => {
    if (!searchQ) return true;
    const q = searchQ.toLowerCase();
    if (item.item_name.toLowerCase().includes(q)) return true;
    if (item.variants?.some(v => v.specification?.toLowerCase().includes(q))) return true;
    return false;
  });

  const totalVariants = items.reduce((sum, item) => sum + (item.variants?.length || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="db-spec-database">
      {/* Header */}
      <div className="section-heading-row flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-base font-semibold font-['Manrope']">Item & Cost Sheet</h3>
          <p className="text-xs text-[#6B7280] mt-0.5">{items.length} items · {totalVariants} specification variants</p>
        </div>
        <div className="flex gap-2">
          {items.length === 0 && (
            <button onClick={seedDatabase} data-testid="seed-db-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8E6E1] text-[#2C2C2E] text-xs font-medium hover:bg-[#D4D0C8] transition-colors">
              <Upload size={13} /> Load Ratelist
            </button>
          )}
          <button onClick={openAddItem} data-testid="add-db-item-btn"
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors">
            <Plus size={13} /> Add New Item
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
        <Input placeholder="Search items or specifications..." value={searchQ} onChange={e => setSearchQ(e.target.value)}
          className="pl-9 h-9 rounded-lg border-[#E8E6E1] text-sm" data-testid="db-search" />
      </div>

      {/* GST Note */}
      <div className="px-3 py-2 bg-[#C67D5B]/10 border border-[#C67D5B]/20 rounded-lg">
        <p className="text-xs text-[#C67D5B]">All rates are exclusive of 18% GST. Rates are tentative and may vary based on site condition, selection, sizes, and design.</p>
      </div>

      {/* Items List */}
      <div className="space-y-2">
        {filtered.map(item => {
          const isExpanded = expandedItems[item.id];
          const activeVariants = item.variants?.filter(v => !v.archived) || [];
          const rateRange = activeVariants.length > 0 
            ? `Rs ${formatINR(Math.min(...activeVariants.map(v => v.rate)))} - Rs ${formatINR(Math.max(...activeVariants.map(v => v.rate)))}`
            : '-';
          
          return (
            <div key={item.id} className="glass-card rounded-xl overflow-hidden" data-testid={`db-item-${item.id}`}>
              {/* Item Header Row */}
              <div 
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#FAFAF8]/50 transition-colors group"
                onClick={() => toggleExpand(item.id)}
              >
                <button className="p-0.5">
                  {isExpanded ? <ChevronDown size={16} className="text-[#6B7280]" /> : <ChevronRight size={16} className="text-[#6B7280]" />}
                </button>
                <span className="w-8 text-xs font-medium text-[#6B7280]">{item.sr_no}.</span>
                <span className="flex-1 text-sm font-semibold text-[#2C2C2E]">{item.item_name}</span>
                <Badge className="bg-[#E8E6E1] text-[#2C2C2E] text-[10px]">{activeVariants.length} variants</Badge>
                <span className="text-xs text-[#6B7280] w-40 text-right">{rateRange}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={(e) => { e.stopPropagation(); openEditItem(item); }} className="p-1.5 rounded hover:bg-[#E8E6E1]" title="Edit item" data-testid={`edit-item-${item.id}`}>
                    <Edit2 size={13} className="text-[#6B7280]" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); archiveItem(item.id); }} className="p-1.5 rounded hover:bg-[#C4727F]/10" title="Delete item" data-testid={`delete-item-${item.id}`}>
                    <Trash2 size={13} className="text-[#C4727F]" />
                  </button>
                </div>
              </div>

              {/* Expanded Variants */}
              {isExpanded && (
                <div className="border-t border-[#E8E6E1]">
                  {/* Notes */}
                  {item.notes && (
                    <div className="px-4 py-2 bg-[#FAFAF8] border-b border-[#E8E6E1]">
                      <p className="text-[10px] text-[#6B7280]">{item.notes}</p>
                    </div>
                  )}
                  
                  {/* Variants Table */}
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#FAFAF8]">
                        <th className="px-4 py-2 text-left text-[10px] font-semibold text-[#6B7280] uppercase">Specification</th>
                        <th className="px-3 py-2 text-center text-[10px] font-semibold text-[#6B7280] uppercase w-20">UOM</th>
                        <th className="px-3 py-2 text-right text-[10px] font-semibold text-[#6B7280] uppercase w-24">Rate (Rs)</th>
                        <th className="w-24"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeVariants.map(variant => (
                        <tr key={variant.id} className="border-t border-[#E8E6E1] hover:bg-[#FAFAF8]/50 group">
                          <td className="px-4 py-2.5 text-sm text-[#2C2C2E]">{variant.specification}</td>
                          <td className="px-3 py-2.5 text-xs text-[#6B7280] text-center">{variant.uom}</td>
                          <td className="px-3 py-2.5 text-sm font-semibold text-[#2C2C2E] text-right">Rs {formatINR(variant.rate)}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => showRateHistory(variant)} className="p-1.5 rounded hover:bg-[#E8E6E1]" title="Rate History" data-testid={`history-variant-${variant.id}`}>
                                <History size={13} className="text-[#6B7280]" />
                              </button>
                              <button onClick={() => openEditVariant(item.id, variant)} className="p-1.5 rounded hover:bg-[#E8E6E1]" title="Edit variant" data-testid={`edit-variant-${variant.id}`}>
                                <Edit2 size={13} className="text-[#6B7280]" />
                              </button>
                              <button onClick={() => archiveVariant(item.id, variant.id)} className="p-1.5 rounded hover:bg-[#C4727F]/10" title="Delete variant" data-testid={`delete-variant-${variant.id}`}>
                                <Trash2 size={13} className="text-[#C4727F]" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Add Variant Button */}
                  <button 
                    onClick={() => openAddVariant(item.id)}
                    className="w-full px-4 py-2 text-xs text-[#3A8C8C] hover:bg-[#3A8C8C]/5 transition-colors flex items-center gap-1.5 justify-center border-t border-[#E8E6E1]"
                  >
                    <Plus size={12} /> Add Specification Variant
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <Database size={32} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-3" />
            <p className="text-sm text-[#6B7280]">No items found</p>
            {items.length === 0 && (
              <button onClick={seedDatabase} className="text-xs text-[#3A8C8C] hover:underline mt-2">
                Load Item & Cost Sheet to get started
              </button>
            )}
          </div>
        )}
      </div>

      {/* Item Modal */}
      <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">{editingItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Sr. No.</Label>
                <Input type="number" value={itemForm.sr_no} onChange={e => setItemForm({...itemForm, sr_no: parseInt(e.target.value) || 0})}
                  className="mt-1 h-9 rounded-lg border-[#D4D0C8]" />
              </div>
              <div className="col-span-3">
                <Label className="text-xs text-[#6B7280] uppercase">Item Name *</Label>
                <Input value={itemForm.item_name} onChange={e => setItemForm({...itemForm, item_name: e.target.value})}
                  placeholder="e.g., False Ceiling, Wardrobe"
                  className="mt-1 h-9 rounded-lg border-[#D4D0C8]" data-testid="db-item-name" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Notes</Label>
              <textarea value={itemForm.notes} onChange={e => setItemForm({...itemForm, notes: e.target.value})}
                placeholder="Hardware brands, material notes, conditions..."
                className="mt-1 w-full h-20 px-3 py-2 text-sm border border-[#D4D0C8] rounded-lg resize-none" />
            </div>
            <button onClick={saveItem} data-testid="save-db-item-btn"
              className="w-full h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors">
              {editingItem ? 'Update Item' : 'Create Item'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Variant Modal */}
      <Dialog open={showVariantModal} onOpenChange={setShowVariantModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">{editingVariant ? 'Edit Variant' : 'Add Specification Variant'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Specification *</Label>
              <textarea value={variantForm.specification} onChange={e => setVariantForm({...variantForm, specification: e.target.value})}
                placeholder="e.g., Ply structure with Laminate finish"
                className="mt-1 w-full h-20 px-3 py-2 text-sm border border-[#D4D0C8] rounded-lg resize-none" data-testid="variant-spec" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">UOM</Label>
                <Select value={variantForm.uom} onValueChange={v => setVariantForm({...variantForm, uom: v})}>
                  <SelectTrigger className="mt-1 h-9 rounded-lg border-[#D4D0C8]"><SelectValue /></SelectTrigger>
                  <SelectContent>{uomOptions.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Rate (Rs)</Label>
                <Input type="number" value={variantForm.rate} onChange={e => setVariantForm({...variantForm, rate: parseFloat(e.target.value) || 0})}
                  className="mt-1 h-9 rounded-lg border-[#D4D0C8]" data-testid="variant-rate" />
              </div>
            </div>
            <button onClick={saveVariant} data-testid="save-variant-btn"
              className="w-full h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors">
              {editingVariant ? 'Update Variant' : 'Add Variant'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rate History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-sm">Rate History</DialogTitle>
          </DialogHeader>
          {historyVariant && (
            <div className="mt-2">
              <p className="text-xs text-[#6B7280] mb-3">{historyVariant.specification}</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(historyVariant.rate_history || []).slice().reverse().map((h, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-[#E8E6E1] last:border-0">
                    <span className="text-sm font-semibold text-[#2C2C2E]">Rs {formatINR(h.rate)}</span>
                    <span className="text-xs text-[#6B7280]">{formatDate(h.date)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {confirmDialog}
    </div>
  );
}

// Agency Database Component
function AgencyDatabase() {
  const [confirm, confirmDialog] = useConfirm();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', contact_person: '', phone: '', email: '', address: '', category: '', gst_number: '', notes: '' });

  const categories = ['Carpenter', 'Electrician', 'Plumber', 'Painter', 'Mason', 'Fabricator', 'Glass Work', 'AC/HVAC', 'Modular Kitchen', 'Other'];

  const loadAgencies = useCallback(async () => {
    try {
      const res = await api.get('/agencies');
      setAgencies(res.data);
    } catch { toast.error('Failed to load agencies'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAgencies(); }, [loadAgencies]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', contact_person: '', phone: '', email: '', address: '', category: '', gst_number: '', notes: '' });
    setShowModal(true);
  };

  const openEdit = (agency) => {
    setEditing(agency);
    setForm({
      name: agency.name || '', contact_person: agency.contact_person || '', phone: agency.phone || '',
      email: agency.email || '', address: agency.address || '', category: agency.category || '',
      gst_number: agency.gst_number || '', notes: agency.notes || ''
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name) { toast.error('Agency name is required'); return; }
    try {
      if (editing) {
        await api.put(`/agencies/${editing.id}`, form);
        toast.success('Agency updated');
      } else {
        await api.post('/agencies', form);
        toast.success('Agency created');
      }
      setShowModal(false);
      loadAgencies();
    } catch { toast.error('Failed to save agency'); }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: 'Archive agency?', message: 'It will be removed from the active agency list.', confirmLabel: 'Archive' }))) return;
    try {
      await api.delete(`/agencies/${id}`);
      toast.success('Agency archived');
      loadAgencies();
    } catch { toast.error('Failed to archive agency'); }
  };

  const filtered = agencies.filter(a => !searchQ || a.name?.toLowerCase().includes(searchQ.toLowerCase()) || a.category?.toLowerCase().includes(searchQ.toLowerCase()));

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="agency-database">
      <div className="section-heading-row flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold font-['Manrope']">Agency Database</h3>
          <p className="text-xs text-[#6B7280] mt-0.5">{agencies.length} agencies</p>
        </div>
        <button onClick={openAdd} className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors">
          <Plus size={13} /> Add Agency
        </button>
      </div>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
        <Input placeholder="Search agencies..." value={searchQ} onChange={e => setSearchQ(e.target.value)} className="pl-9 h-9 rounded-lg border-[#E8E6E1] text-sm" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(agency => (
          <div key={agency.id} className="glass-card rounded-xl p-4 group" data-testid={`agency-${agency.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-sm font-semibold text-[#2C2C2E]">{agency.name}</h4>
                {agency.category && <Badge className="bg-[#3A8C8C]/10 text-[#3A8C8C] text-[9px] mt-1">{agency.category}</Badge>}
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => openEdit(agency)} className="p-1 rounded hover:bg-[#E8E6E1]"><Edit2 size={12} className="text-[#6B7280]" /></button>
                <button onClick={() => remove(agency.id)} className="p-1 rounded hover:bg-[#C4727F]/10"><Trash2 size={12} className="text-[#C4727F]" /></button>
              </div>
            </div>
            {agency.contact_person && <p className="text-xs text-[#6B7280] mt-2">{agency.contact_person}</p>}
            {agency.phone && <p className="text-xs text-[#6B7280]">{agency.phone}</p>}
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12">
          <Building2 size={32} className="text-[#D4D0C8] mx-auto mb-3" />
          <p className="text-sm text-[#6B7280]">No agencies yet</p>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-['Manrope']">{editing ? 'Edit Agency' : 'Add Agency'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Agency Name *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 h-9 rounded-lg border-[#D4D0C8]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Contact Person</Label>
                <Input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} className="mt-1 h-9 rounded-lg border-[#D4D0C8]" />
              </div>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Category</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger className="mt-1 h-9 rounded-lg border-[#D4D0C8]"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Phone</Label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1 h-9 rounded-lg border-[#D4D0C8]" />
              </div>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Email</Label>
                <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 h-9 rounded-lg border-[#D4D0C8]" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">GST Number</Label>
              <Input value={form.gst_number} onChange={e => setForm({...form, gst_number: e.target.value})} className="mt-1 h-9 rounded-lg border-[#D4D0C8]" />
            </div>
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Address</Label>
              <textarea value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="mt-1 w-full h-16 px-3 py-2 text-sm border border-[#D4D0C8] rounded-lg resize-none" />
            </div>
            <button onClick={save} className="w-full h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors">
              {editing ? 'Update Agency' : 'Create Agency'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {confirmDialog}
    </div>
  );
}

// Client Database Component
function ClientDatabase() {
  const [confirm, confirmDialog] = useConfirm();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', notes: '' });

  const loadClients = useCallback(async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data);
    } catch { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', phone: '', email: '', address: '', notes: '' });
    setShowModal(true);
  };

  const openEdit = (client) => {
    setEditing(client);
    setForm({ name: client.name || '', phone: client.phone || '', email: client.email || '', address: client.address || '', notes: client.notes || '' });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name) { toast.error('Client name is required'); return; }
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, form);
        toast.success('Client updated');
      } else {
        await api.post('/clients', form);
        toast.success('Client created');
      }
      setShowModal(false);
      loadClients();
    } catch { toast.error('Failed to save client'); }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: 'Archive client?', message: 'They will be removed from the active client list.', confirmLabel: 'Archive' }))) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success('Client archived');
      loadClients();
    } catch { toast.error('Failed to archive client'); }
  };

  const filtered = clients.filter(c => !searchQ || c.name?.toLowerCase().includes(searchQ.toLowerCase()) || c.email?.toLowerCase().includes(searchQ.toLowerCase()));

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="client-database">
      <div className="section-heading-row flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold font-['Manrope']">Client Database</h3>
          <p className="text-xs text-[#6B7280] mt-0.5">{clients.length} clients</p>
        </div>
        <button onClick={openAdd} className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors">
          <Plus size={13} /> Add Client
        </button>
      </div>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
        <Input placeholder="Search clients..." value={searchQ} onChange={e => setSearchQ(e.target.value)} className="pl-9 h-9 rounded-lg border-[#E8E6E1] text-sm" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(client => (
          <div key={client.id} className="glass-card rounded-xl p-4 group" data-testid={`client-${client.id}`}>
            <div className="flex items-start justify-between">
              <h4 className="text-sm font-semibold text-[#2C2C2E]">{client.name}</h4>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => openEdit(client)} className="p-1 rounded hover:bg-[#E8E6E1]"><Edit2 size={12} className="text-[#6B7280]" /></button>
                <button onClick={() => remove(client.id)} className="p-1 rounded hover:bg-[#C4727F]/10"><Trash2 size={12} className="text-[#C4727F]" /></button>
              </div>
            </div>
            {client.phone && <p className="text-xs text-[#6B7280] mt-2">{client.phone}</p>}
            {client.email && <p className="text-xs text-[#6B7280]">{client.email}</p>}
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12">
          <Users size={32} className="text-[#D4D0C8] mx-auto mb-3" />
          <p className="text-sm text-[#6B7280]">No clients yet</p>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-['Manrope']">{editing ? 'Edit Client' : 'Add Client'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Client Name *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 h-9 rounded-lg border-[#D4D0C8]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Phone</Label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1 h-9 rounded-lg border-[#D4D0C8]" />
              </div>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Email</Label>
                <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 h-9 rounded-lg border-[#D4D0C8]" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Address</Label>
              <textarea value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="mt-1 w-full h-16 px-3 py-2 text-sm border border-[#D4D0C8] rounded-lg resize-none" />
            </div>
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Notes</Label>
              <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1 w-full h-16 px-3 py-2 text-sm border border-[#D4D0C8] rounded-lg resize-none" />
            </div>
            <button onClick={save} className="w-full h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors">
              {editing ? 'Update Client' : 'Create Client'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {confirmDialog}
    </div>
  );
}

// Main Databases Page
export default function DatabasesPage() {
  const { user } = useAuth();
  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="databases-page">
      <h1 className="section-heading text-xl font-semibold font-['Manrope']">Databases</h1>
      
      <Tabs defaultValue="db-spec" className="w-full">
        <TabsList className="bg-[#E8E6E1]/50 rounded-xl p-1 mb-6">
          <TabsTrigger value="db-spec" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Database size={13} className="mr-1.5" /> Item & Cost Sheet
          </TabsTrigger>
          <TabsTrigger value="materials" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="db-tab-materials">
            <Layers size={13} className="mr-1.5" /> Material List
          </TabsTrigger>
          <TabsTrigger value="templates" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="db-tab-templates">
            <FileStack size={13} className="mr-1.5" /> Templates
          </TabsTrigger>
          <TabsTrigger value="brief-templates" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="db-tab-brief-templates">
            <BookOpen size={13} className="mr-1.5" /> Brief Templates
          </TabsTrigger>
          {isOwner(user) && (
            <TabsTrigger value="doc-templates" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="db-tab-doc-templates">
              <FileText size={13} className="mr-1.5" /> Proposal &amp; Invoice
            </TabsTrigger>
          )}
          <TabsTrigger value="agencies" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Building2 size={13} className="mr-1.5" /> Agency Database
          </TabsTrigger>
          <TabsTrigger value="clients" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Users size={13} className="mr-1.5" /> Client Database
          </TabsTrigger>
        </TabsList>

        <TabsContent value="db-spec">
          <DBSpecDatabase />
        </TabsContent>

        <TabsContent value="materials">
          <MaterialMasterDatabase />
        </TabsContent>

        <TabsContent value="templates">
          <TemplatesManager />
        </TabsContent>

        <TabsContent value="brief-templates">
          <BriefTemplatesManager />
        </TabsContent>

        <TabsContent value="doc-templates">
          <DocumentTemplatesManager />
        </TabsContent>

        <TabsContent value="agencies">
          <AgencyDatabase />
        </TabsContent>

        <TabsContent value="clients">
          <ClientDatabase />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MaterialMasterDatabase() {
  const [confirm, confirmDialog] = useConfirm();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/materials');
      setMaterials(res.data || []);
    } catch { toast.error('Failed to load materials'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) { toast.error('Item name required'); return; }
    try {
      await api.post('/materials', { item_name: name.trim() });
      toast.success('Material added');
      setName(''); setShowAdd(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: 'Archive material?', message: 'It will be removed from the material master list.', confirmLabel: 'Archive' }))) return;
    try {
      await api.delete(`/materials/${id}`);
      toast.success('Archived');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Admin only');
    }
  };

  const filtered = materials.filter(m => !search || m.item_name.toLowerCase().includes(search.toLowerCase()));
  const totalUsage = materials.reduce((s, m) => s + (m.usage_count || 0), 0);
  const popular = [...materials].filter(m => m.usage_count > 0).slice(0, 3);

  return (
    <div className="space-y-4" data-testid="material-master">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="glass-card rounded-xl p-4">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Total Materials</p>
          <p className="text-lg font-bold text-[#2C2C2E] font-['Manrope'] mt-1" data-testid="material-master-count">{materials.length}</p>
        </div>
        <div className="glass-card rounded-xl p-4">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Total Usages</p>
          <p className="text-lg font-bold text-[#2C2C2E] font-['Manrope'] mt-1">{totalUsage}</p>
        </div>
        <div className="glass-card rounded-xl p-4 col-span-2">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold mb-1">Most Popular</p>
          {popular.length ? (
            <div className="flex flex-wrap gap-1.5">
              {popular.map(p => (
                <Badge key={p.id} className="bg-[#3A8C8C]/10 text-[#3A8C8C] text-[9px]">
                  {p.item_name} · {p.usage_count}×
                </Badge>
              ))}
            </div>
          ) : <p className="text-xs text-[#6B7280]">No usage yet</p>}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search materials"
            className="w-full h-9 pl-9 pr-3 rounded-lg border border-[#D4D0C8] bg-white text-xs focus:outline-none focus:border-[#3A8C8C]"
            data-testid="material-master-search"
          />
        </div>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors"
          data-testid="material-master-add">
          <Plus size={14} /> Add Material
        </button>
      </div>

      <div className="glass-card rounded-2xl overflow-hidden" data-testid="material-master-table">
        <table className="w-full">
          <thead>
            <tr className="bg-[#FAFAF8] border-b border-[#E8E6E1]">
              <th className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold text-left px-4 py-2.5">Item Name</th>
              <th className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold text-center px-4 py-2.5 w-32">Usage Count</th>
              <th className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold text-left px-4 py-2.5 w-44">Last Used</th>
              <th className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold text-right px-4 py-2.5 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="py-8 text-center text-xs text-[#6B7280]">Loading…</td></tr>}
            {!loading && filtered.map(m => (
              <tr key={m.id} className="border-b border-[#E8E6E1]/60 hover:bg-[#FAFAF8]/40 group" data-testid={`material-master-row-${m.id}`}>
                <td className="px-4 py-2.5 text-sm font-medium text-[#2C2C2E]">{m.item_name}</td>
                <td className="px-4 py-2.5 text-center">
                  <Badge className={`text-[9px] ${m.usage_count > 0 ? 'bg-[#3A8C8C]/10 text-[#3A8C8C]' : 'bg-[#E8E6E1] text-[#6B7280]'}`}>
                    {m.usage_count || 0}
                  </Badge>
                </td>
                <td className="px-4 py-2.5 text-xs text-[#6B7280]">{m.last_used_at ? formatDate(m.last_used_at) : '—'}</td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => remove(m.id)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-[#C4727F]/10 transition-all"
                    data-testid={`material-master-archive-${m.id}`}>
                    <Trash2 size={12} className="text-[#C4727F]" />
                  </button>
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={4} className="py-8 text-center text-xs text-[#6B7280]">
                {search ? 'No materials match search' : 'No materials yet. Add one or use them on a project to populate this list.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showAdd} onOpenChange={(o) => { setShowAdd(o); if (!o) setName(''); }}>
        <DialogContent className="max-w-md" data-testid="material-master-add-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope']">Add Material to Master</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Item Name *</Label>
              <Input value={name} onChange={e => setName(e.target.value)}
                className="mt-1 h-10 rounded-xl border-[#D4D0C8]"
                placeholder='e.g. "Royal Touch Laminate – Walnut"'
                data-testid="material-master-name"
                autoFocus />
              <p className="text-[10px] text-[#6B7280] mt-1">Soft-uniqueness: exact duplicates are blocked.</p>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => { setShowAdd(false); setName(''); }}
                className="flex-1 h-10 rounded-full border border-[#D4D0C8] text-sm font-medium text-[#6B7280] hover:bg-[#E8E6E1]">Cancel</button>
              <button onClick={create}
                className="flex-1 h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A]"
                data-testid="material-master-save">
                Add
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {confirmDialog}
    </div>
  );
}
